import React, { useState, useMemo } from 'react';
import { useFamily } from '../../context/FamilyContext';
import { useAuth } from '../../context/AuthContext';
import { settleExpensesBulk } from '@coparent/shared/src/services/expenseService';
import { calculateBalance } from '@coparent/shared/src/services/expenseService';
import { formatCurrency, getCategoryIcon } from '../../utils/expenseUtils';
import { formatDate } from '@coparent/shared/src/utils/dateUtils';
import { SETTLEMENT_METHODS } from '@coparent/shared/src/utils/constants';
import { HiX, HiCheck, HiCheckCircle } from 'react-icons/hi';
import toast from 'react-hot-toast';

const BulkSettleModal = ({ expenses, onClose }) => {
  const { familyId, family, getParticipantProfile } = useFamily();
  const { currentUser } = useAuth();
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [method, setMethod] = useState('');
  const [settling, setSettling] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const pendingExpenses = useMemo(() =>
    expenses.filter(e => e.status === 'pending'),
    [expenses]
  );

  const balance = useMemo(() => {
    if (!family || family.parentIds.length < 2) return null;
    return calculateBalance(expenses, family.parentIds, family.householdMembers || {});
  }, [expenses, family]);

  const allSelected = selectedIds.size === pendingExpenses.length && pendingExpenses.length > 0;

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingExpenses.map(e => e.id)));
    }
  };

  const toggleOne = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedTotal = useMemo(() => {
    return pendingExpenses
      .filter(e => selectedIds.has(e.id))
      .reduce((sum, e) => sum + e.owedAmount, 0);
  }, [pendingExpenses, selectedIds]);

  const handleSettle = async () => {
    if (!selectedIds.size || !method) return;
    setSettling(true);
    try {
      await settleExpensesBulk(familyId, [...selectedIds], method, {
        paidBy: balance?.owerId,
        paidTo: balance?.oweeId,
        totalAmount: selectedTotal,
        createdBy: currentUser.uid
      });
      toast.success(`${selectedIds.size} expense${selectedIds.size > 1 ? 's' : ''} settled`);
      onClose();
    } catch (error) {
      toast.error('Failed to settle expenses');
    } finally {
      setSettling(false);
    }
  };

  if (!pendingExpenses.length) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content max-w-md" onClick={e => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold font-display text-ink-900">Settle Up</h2>
            <button onClick={onClose} className="text-ink-400 hover:text-ink-600">
              <HiX className="text-xl" />
            </button>
          </div>
          <p className="text-sm text-ink-500 text-center py-8">No pending expenses to settle.</p>
        </div>
      </div>
    );
  }

  const owerProfile = balance ? family.parentProfiles[balance.owerId] : null;
  const oweeProfile = balance ? family.parentProfiles[balance.oweeId] : null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content max-w-lg" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold font-display text-ink-900">Settle Up</h2>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-600">
            <HiX className="text-xl" />
          </button>
        </div>

        {/* Balance summary */}
        {balance && balance.amount > 0 && (
          <div className="bg-surface-50 rounded-xl p-4 mb-5 text-center">
            <p className="text-sm text-ink-500">
              <span className="font-semibold" style={{ color: owerProfile?.color }}>
                {balance.owerId === currentUser.uid ? 'You' : owerProfile?.displayName}
              </span>
              {' owe '}
              <span className="font-semibold" style={{ color: oweeProfile?.color }}>
                {balance.oweeId === currentUser.uid ? 'you' : oweeProfile?.displayName}
              </span>
            </p>
            <p className="text-2xl font-bold text-ink-900 mt-1">{formatCurrency(balance.amount)}</p>
          </div>
        )}

        {/* Select all */}
        <div className="flex items-center justify-between mb-3">
          <button
            onClick={toggleAll}
            className="flex items-center gap-2 text-sm font-medium text-ink-600 hover:text-ink-900 transition-colors"
          >
            <span className={`w-5 h-5 rounded flex items-center justify-center border-2 transition-colors ${
              allSelected ? 'bg-canopy border-canopy text-white' : 'border-ink-200'
            }`}>
              {allSelected && <HiCheck className="text-xs" />}
            </span>
            Select all ({pendingExpenses.length})
          </button>
          {selectedIds.size > 0 && (
            <p className="text-sm text-ink-500">
              {selectedIds.size} selected &middot; {formatCurrency(selectedTotal)} owed
            </p>
          )}
        </div>

        {/* Expense list */}
        <div className="max-h-64 overflow-y-auto space-y-1.5 mb-5">
          {pendingExpenses.map(expense => {
            const selected = selectedIds.has(expense.id);
            const paidByProfile = getParticipantProfile(expense.paidBy);
            return (
              <button
                key={expense.id}
                onClick={() => toggleOne(expense.id)}
                className={`w-full flex items-center gap-3 p-3 rounded-xl text-left transition-colors ${
                  selected ? 'bg-canopy/5 ring-1 ring-canopy/20' : 'bg-surface-50 hover:bg-surface-100'
                }`}
              >
                <span className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center border-2 transition-colors ${
                  selected ? 'bg-canopy border-canopy text-white' : 'border-ink-200'
                }`}>
                  {selected && <HiCheck className="text-xs" />}
                </span>
                <span className="text-lg">{getCategoryIcon(expense.category)}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-ink-900 truncate">{expense.description}</p>
                  <p className="text-xs text-ink-400">
                    {formatDate(expense.date)} &middot; Paid by{' '}
                    <span style={{ color: paidByProfile?.color }}>{paidByProfile?.displayName}</span>
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-sm font-bold text-ink-900">{formatCurrency(expense.amount)}</p>
                  <p className="text-[0.6875rem] text-ink-400">{formatCurrency(expense.owedAmount)} owed</p>
                </div>
              </button>
            );
          })}
        </div>

        {/* Payment method */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-ink-700 mb-2">Payment method</label>
          <div className="flex flex-wrap gap-2">
            {SETTLEMENT_METHODS.map(m => (
              <button
                key={m.value}
                onClick={() => setMethod(m.value)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  method === m.value
                    ? 'bg-canopy text-white'
                    : 'bg-surface-50 text-ink-600 hover:bg-surface-100'
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {/* Action */}
        <button
          onClick={() => setShowConfirm(true)}
          disabled={!selectedIds.size || !method}
          className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <HiCheckCircle className="text-lg" />
          Settle {selectedIds.size} expense{selectedIds.size !== 1 ? 's' : ''} — {formatCurrency(selectedTotal)}
        </button>

        {/* Confirmation modal */}
        {showConfirm && (
          <div className="modal-overlay" onClick={() => !settling && setShowConfirm(false)}>
            <div className="modal-content max-w-sm" onClick={e => e.stopPropagation()}>
              <h3 className="text-lg font-bold font-display text-ink-900 mb-4">Confirm settlement</h3>
              <div className="bg-surface-50 rounded-xl p-4 mb-5 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-500">Expenses</span>
                  <span className="text-sm font-semibold text-ink-900">{selectedIds.size} selected</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-500">Total owed</span>
                  <span className="text-sm font-bold text-ink-900">{formatCurrency(selectedTotal)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-ink-500">Method</span>
                  <span className="text-sm font-semibold text-ink-900">
                    {SETTLEMENT_METHODS.find(m => m.value === method)?.label}
                  </span>
                </div>
              </div>
              <p className="text-xs text-ink-400 mb-5">This will mark all selected expenses as settled. This action cannot be undone.</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowConfirm(false)}
                  disabled={settling}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSettle}
                  disabled={settling}
                  className="btn-primary flex-1 flex items-center justify-center gap-2"
                >
                  <HiCheck className="text-base" />
                  {settling ? 'Settling...' : 'Settle'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkSettleModal;
